import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { speseApi, exchangeRatesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Wallet, Plus, Trash2, Loader2, TrendingUp, Pencil, RefreshCw, 
  Fuel, MapPin, Download, Calendar, BarChart3, GaugeCircle, Camera
} from 'lucide-react';
import { toast } from 'sonner';
import { FotoUploader } from '../components/FotoUploader';
import { OcrCamera } from '../components/OcrCamera';
import { ListSkeleton, SpesaCardSkeleton } from '../components/Skeleton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIE_SPESE = [
  'Carburante', 'Assicurazione', 'Bollo', 'Manutenzione', 
  'Gomme', 'Pedaggi', 'Parcheggio', 'Accessori', 'Equipaggiamento', 'Altro'
];

const VALUTE = [
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'CHF', symbol: 'CHF', name: 'Franco Svizzero' },
  { code: 'USD', symbol: '$', name: 'Dollaro USA' },
];

const CHART_COLORS = ['#FF5F1F', '#FF8C00', '#FFB347', '#FFCC00', '#FFE135', '#98D8C8', '#7EC8E3', '#A78BFA', '#F472B6', '#6B7280'];

const MONTHS = [
  { value: 'all', label: 'Tutti i mesi' },
  { value: '01', label: 'Gennaio' },
  { value: '02', label: 'Febbraio' },
  { value: '03', label: 'Marzo' },
  { value: '04', label: 'Aprile' },
  { value: '05', label: 'Maggio' },
  { value: '06', label: 'Giugno' },
  { value: '07', label: 'Luglio' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Settembre' },
  { value: '10', label: 'Ottobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Dicembre' }
];

const currentYear = new Date().getFullYear();
const YEARS = [
  { value: 'all', label: 'Tutti gli anni' },
  ...Array.from({ length: 10 }, (_, i) => ({
    value: String(currentYear - i),
    label: String(currentYear - i)
  }))
];

export default function Spese() {
  const { t } = useTranslation();
  const { user, motoList, selectedMotoId } = useAuth();
  const [spese, setSpese] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fuelDialogOpen, setFuelDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [exchangeRates, setExchangeRates] = useState({});
  const [loadingRates, setLoadingRates] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [reportPeriod, setReportPeriod] = useState('month');
  const [reportData, setReportData] = useState(null);
  const [fuelStats, setFuelStats] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);

  // Standard expense form
  const [formData, setFormData] = useState({
    moto_id: '',
    categoria: '',
    descrizione: '',
    importo: 0,
    valuta: 'CHF',
    data: '',
    foto_urls: []
  });

  // Fuel-specific form
  const [fuelData, setFuelData] = useState({
    moto_id: '',
    km_contachilometri: 0,
    litri: 0,
    importo: 0,
    prezzo_litro: 0,
    valuta: 'CHF',
    tipo_pieno: 'completo',
    ultimo_pieno_non_registrato: false,
    latitudine: null,
    longitudine: null,
    indirizzo_stazione: '',
    data: '',
    foto_urls: []
  });

  useEffect(() => {
    loadSpese();
    loadExchangeRates();
  }, [selectedMotoId]);

  useEffect(() => {
    if (activeTab === 'report' || activeTab === 'fuel-history') {
      loadReport();
      loadFuelStats();
    }
  }, [activeTab, reportPeriod, selectedMotoId]);

  const loadSpese = async () => {
    try {
      setLoading(true);
      const response = await speseApi.getAll(selectedMotoId || undefined);
      setSpese(response.data);
    } catch (error) {
      toast.error(t('errors.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadExchangeRates = async () => {
    try {
      setLoadingRates(true);
      const response = await exchangeRatesApi.getRates();
      setExchangeRates(response.data.rates || {});
    } catch (error) {
      console.error('Error loading exchange rates:', error);
    } finally {
      setLoadingRates(false);
    }
  };

  const loadReport = async () => {
    try {
      const response = await speseApi.getReport(reportPeriod, selectedMotoId || undefined);
      setReportData(response.data);
    } catch (error) {
      console.error('Error loading report:', error);
    }
  };

  const loadFuelStats = async () => {
    try {
      const response = await speseApi.getFuelStats(selectedMotoId || undefined);
      setFuelStats(response.data);
    } catch (error) {
      console.error('Error loading fuel stats:', error);
    }
  };

  const resetForm = () => {
    const today = new Date();
    const formattedDate = `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(2, '0')}.${today.getFullYear()}`;
    setFormData({
      moto_id: selectedMotoId || '',
      categoria: '',
      descrizione: '',
      importo: 0,
      valuta: user?.currency || 'CHF',
      data: formattedDate,
      foto_urls: []
    });
    setEditingId(null);
  };

  const resetFuelForm = () => {
    const today = new Date();
    const formattedDate = `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(2, '0')}.${today.getFullYear()}`;
    
    // Get last km from moto
    const selectedMoto = motoList.find(m => m.id === selectedMotoId);
    
    setFuelData({
      moto_id: selectedMotoId || '',
      km_contachilometri: selectedMoto?.km_attuali || 0,
      litri: 0,
      importo: 0,
      prezzo_litro: 0,
      valuta: user?.currency || 'CHF',
      tipo_pieno: 'completo',
      ultimo_pieno_non_registrato: false,
      latitudine: null,
      longitudine: null,
      indirizzo_stazione: '',
      data: formattedDate,
      foto_urls: []
    });
  };

  const handleOpenDialog = (spesa = null) => {
    if (spesa) {
      setFormData({
        moto_id: spesa.moto_id || '',
        categoria: spesa.categoria,
        descrizione: spesa.descrizione,
        importo: spesa.importo,
        valuta: spesa.valuta || 'CHF',
        data: spesa.data,
        foto_urls: spesa.foto_urls || []
      });
      setEditingId(spesa.id);
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleOpenFuelDialog = () => {
    resetFuelForm();
    setFuelDialogOpen(true);
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t('expenses.fuel.gpsNotSupported'));
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setFuelData(prev => ({
          ...prev,
          latitudine: latitude,
          longitudine: longitude
        }));
        
        // Try to get address via reverse geocoding
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          if (data.display_name) {
            setFuelData(prev => ({
              ...prev,
              indirizzo_stazione: data.display_name.split(',').slice(0, 3).join(', ')
            }));
          }
        } catch (e) {
          console.error('Reverse geocoding failed:', e);
        }
        
        setGettingLocation(false);
        toast.success(t('expenses.fuel.locationFound'));
      },
      (error) => {
        setGettingLocation(false);
        toast.error(t('expenses.fuel.locationError'));
      },
      { enableHighAccuracy: true }
    );
  };

  const calculatePricePerLiter = () => {
    if (fuelData.importo > 0 && fuelData.litri > 0) {
      const ppl = fuelData.importo / fuelData.litri;
      setFuelData(prev => ({ ...prev, prezzo_litro: Math.round(ppl * 1000) / 1000 }));
    }
  };

  const calculateTotal = () => {
    if (fuelData.prezzo_litro > 0 && fuelData.litri > 0) {
      const total = fuelData.prezzo_litro * fuelData.litri;
      setFuelData(prev => ({ ...prev, importo: Math.round(total * 100) / 100 }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.categoria || !formData.descrizione || !formData.importo || !formData.data) {
      toast.error(t('errors.fillRequired'));
      return;
    }

    setSaving(true);
    try {
      const dataToSend = { ...formData, moto_id: formData.moto_id === '__none__' ? null : (formData.moto_id || null) };
      
      if (editingId) {
        await speseApi.update(editingId, dataToSend);
        toast.success(t('success.updated'));
      } else {
        await speseApi.create(dataToSend);
        toast.success(t('success.saved'));
      }
      setDialogOpen(false);
      loadSpese();
    } catch (error) {
      toast.error(t('errors.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleFuelSubmit = async (e) => {
    e.preventDefault();
    if (!fuelData.moto_id || !fuelData.km_contachilometri || !fuelData.litri || !fuelData.importo) {
      toast.error(t('errors.fillRequired'));
      return;
    }

    setSaving(true);
    try {
      const dataToSend = {
        ...fuelData,
        categoria: 'Carburante',
        descrizione: `Rifornimento ${fuelData.tipo_pieno === 'completo' ? 'pieno' : 'parziale'} - ${fuelData.litri}L`
      };
      
      await speseApi.create(dataToSend);
      toast.success(t('expenses.fuel.saved'));
      setFuelDialogOpen(false);
      loadSpese();
      loadFuelStats();
    } catch (error) {
      toast.error(t('errors.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('expenses.deleteConfirm'))) return;
    try {
      await speseApi.delete(id);
      toast.success(t('success.deleted'));
      loadSpese();
    } catch (error) {
      toast.error(t('errors.deleteError'));
    }
  };

  const generatePDF = () => {
    if (!reportData) return;
    
    const doc = new jsPDF();
    const selectedMoto = motoList.find(m => m.id === selectedMotoId);
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(255, 95, 31);
    doc.text('VAULT RIDE - Report Spese', 14, 20);
    
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text(`Moto: ${selectedMoto ? `${selectedMoto.marca} ${selectedMoto.modello}` : 'Tutte'}`, 14, 30);
    doc.text(`Periodo: ${reportPeriod === 'month' ? 'Mensile' : 'Annuale'}`, 14, 37);
    doc.text(`Generato: ${new Date().toLocaleDateString('it-IT')}`, 14, 44);
    
    // Category totals
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text('Totali per Categoria', 14, 58);
    
    const categoryData = Object.entries(reportData.category_totals || {}).map(([cat, val]) => [
      cat.charAt(0).toUpperCase() + cat.slice(1),
      `${val.toFixed(2)} ${user?.currency || 'CHF'}`
    ]);
    
    autoTable(doc, {
      startY: 62,
      head: [['Categoria', 'Totale']],
      body: categoryData,
      theme: 'striped',
      headStyles: { fillColor: [255, 95, 31] }
    });
    
    // Grand total
    doc.setFontSize(12);
    doc.text(
      `Totale Generale: ${reportData.grand_total?.toFixed(2)} ${user?.currency || 'CHF'}`,
      14,
      doc.lastAutoTable.finalY + 10
    );
    
    // Fuel stats if available
    if (fuelStats && fuelStats.avg_consumption_km_l) {
      doc.setFontSize(14);
      doc.text('Statistiche Carburante', 14, doc.lastAutoTable.finalY + 25);
      
      const fuelData = [
        ['Rifornimenti totali', fuelStats.total_refuels],
        ['Litri totali', `${fuelStats.total_liters} L`],
        ['Consumo medio', `${fuelStats.avg_consumption_km_l} km/L`],
        ['Costo per km', `${fuelStats.cost_per_km} ${user?.currency || 'CHF'}`]
      ];
      
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 30,
        head: [['Statistica', 'Valore']],
        body: fuelData,
        theme: 'striped',
        headStyles: { fillColor: [255, 95, 31] }
      });
    }
    
    doc.save(`vault-ride-report-${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success(t('expenses.report.downloaded'));
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('it-CH').format(num);
  };

  const formatCurrency = (amount, currency = 'CHF') => {
    const symbol = VALUTE.find(v => v.code === currency)?.symbol || currency;
    return `${symbol} ${formatNumber(amount)}`;
  };

  const getMotoName = (motoId) => {
    const moto = motoList.find(m => m.id === motoId);
    return moto ? `${moto.marca} ${moto.modello}` : '-';
  };

  const filteredSpese = spese.filter(s => {
    // Filter by category
    if (filterCategoria !== 'all' && s.categoria.toLowerCase() !== filterCategoria.toLowerCase()) {
      return false;
    }
    
    // Filter by month and year (data format: DD.MM.YYYY)
    if (s.data && (filterMonth !== 'all' || filterYear !== 'all')) {
      const parts = s.data.split('.');
      if (parts.length === 3) {
        const spesaMonth = parts[1]; // MM
        const spesaYear = parts[2];  // YYYY
        
        if (filterMonth !== 'all' && spesaMonth !== filterMonth) {
          return false;
        }
        if (filterYear !== 'all' && spesaYear !== filterYear) {
          return false;
        }
      }
    }
    
    return true;
  });

  const totaleSpese = filteredSpese.reduce((sum, s) => sum + s.importo, 0);

  // Prepare chart data
  const pieChartData = reportData?.category_totals 
    ? Object.entries(reportData.category_totals).map(([name, value]) => ({ name, value }))
    : [];

  const barChartData = reportData?.data 
    ? Object.entries(reportData.data).slice(0, 12).reverse().map(([period, data]) => ({
        period: period.length > 7 ? period.slice(5) : period,
        totale: data.totale || 0,
        carburante: data.Carburante || data.carburante || 0
      }))
    : [];

  if (loading) {
    return (
      <Layout>
        <div className="page-container">
          <div className="page-header">
            <div>
              <h1 className="page-title">{t('nav.expenses')}</h1>
              <p className="page-subtitle">{t('expenses.subtitle')}</p>
            </div>
          </div>
          <ListSkeleton count={5} Component={SpesaCardSkeleton} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">{t('nav.expenses')}</h1>
            <p className="page-subtitle">{t('expenses.subtitle')}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Quick Fuel Button */}
            <Button 
              onClick={handleOpenFuelDialog}
              disabled={!selectedMotoId}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="quick-fuel-btn"
            >
              <Fuel className="w-4 h-4 mr-2" />
              {t('expenses.fuel.quickAdd')}
            </Button>
            
            {/* Regular expense button */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => handleOpenDialog()}
                  className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white"
                  data-testid="add-spesa-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('expenses.addExpense')}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-['Teko'] text-2xl text-white uppercase">
                    {editingId ? t('expenses.editExpense') : t('expenses.addExpense')}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="form-label">{t('expenses.category')} *</Label>
                      <Select value={formData.categoria} onValueChange={(v) => setFormData({...formData, categoria: v})}>
                        <SelectTrigger className="form-input"><SelectValue placeholder={t('common.select')} /></SelectTrigger>
                        <SelectContent className="bg-[#121212] border-[#262626]">
                          {CATEGORIE_SPESE.map(cat => (
                            <SelectItem key={cat} value={cat} className="text-white hover:bg-[#262626]">{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {motoList.length > 0 && (
                      <div>
                        <Label className="form-label">{t('common.motorcycle')}</Label>
                        <Select value={formData.moto_id} onValueChange={(v) => setFormData({...formData, moto_id: v})}>
                          <SelectTrigger className="form-input"><SelectValue placeholder={t('common.select')} /></SelectTrigger>
                          <SelectContent className="bg-[#121212] border-[#262626]">
                            <SelectItem value="__none__" className="text-white hover:bg-[#262626]">-</SelectItem>
                            {motoList.map(moto => (
                              <SelectItem key={moto.id} value={moto.id} className="text-white hover:bg-[#262626]">
                                {moto.marca} {moto.modello}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="form-label">{t('expenses.description')} *</Label>
                    <Input 
                      value={formData.descrizione} 
                      onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                      className="form-input" 
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="form-label">{t('expenses.amount')} *</Label>
                      <Input 
                        type="number" 
                        step="0.01"
                        value={formData.importo} 
                        onChange={(e) => setFormData({...formData, importo: parseFloat(e.target.value) || 0})}
                        onFocus={(e) => e.target.select()}
                        className="form-input" 
                      />
                    </div>
                    <div>
                      <Label className="form-label">{t('expenses.currency')}</Label>
                      <Select value={formData.valuta} onValueChange={(v) => setFormData({...formData, valuta: v})}>
                        <SelectTrigger className="form-input"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#121212] border-[#262626]">
                          {VALUTE.map(v => (
                            <SelectItem key={v.code} value={v.code} className="text-white hover:bg-[#262626]">{v.code}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="form-label">{t('expenses.date')} *</Label>
                      <Input 
                        value={formData.data} 
                        onChange={(e) => setFormData({...formData, data: e.target.value})}
                        placeholder="DD.MM.YYYY"
                        className="form-input" 
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="form-label">{t('expenses.photos')}</Label>
                    <FotoUploader 
                      photos={formData.foto_urls}
                      onPhotosChange={(urls) => setFormData({...formData, foto_urls: urls})}
                      maxPhotos={3}
                    />
                  </div>
                  <Button type="submit" disabled={saving} className="w-full bg-[#FF5F1F] hover:bg-[#FF5F1F]/90">
                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    {t('common.save')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#121212] border border-[#262626] mb-6">
            <TabsTrigger value="list" className="data-[state=active]:bg-[#FF5F1F]">
              <Wallet className="w-4 h-4 mr-2" />
              {t('expenses.list')}
            </TabsTrigger>
            <TabsTrigger value="fuel-history" className="data-[state=active]:bg-[#FF5F1F]">
              <Fuel className="w-4 h-4 mr-2" />
              Rifornimenti
            </TabsTrigger>
            <TabsTrigger value="report" className="data-[state=active]:bg-[#FF5F1F]">
              <BarChart3 className="w-4 h-4 mr-2" />
              {t('expenses.report.title')}
            </TabsTrigger>
          </TabsList>

          {/* List Tab */}
          <TabsContent value="list">
            {/* Filter and Stats */}
            <div className="flex flex-wrap gap-4 mb-6">
              <Select value={filterCategoria} onValueChange={setFilterCategoria}>
                <SelectTrigger className="w-48 bg-[#121212] border-[#262626] text-white">
                  <SelectValue placeholder={t('expenses.filterCategory')} />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#262626]">
                  <SelectItem value="all" className="text-white hover:bg-[#262626]">{t('common.all')}</SelectItem>
                  {CATEGORIE_SPESE.map(cat => (
                    <SelectItem key={cat} value={cat.toLowerCase()} className="text-white hover:bg-[#262626]">{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterMonth} onValueChange={setFilterMonth}>
                <SelectTrigger className="w-40 bg-[#121212] border-[#262626] text-white">
                  <SelectValue placeholder="Mese" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#262626] max-h-[300px]">
                  {MONTHS.map(m => (
                    <SelectItem key={m.value} value={m.value} className="text-white hover:bg-[#262626]">{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterYear} onValueChange={setFilterYear}>
                <SelectTrigger className="w-32 bg-[#121212] border-[#262626] text-white">
                  <SelectValue placeholder="Anno" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#262626] max-h-[300px]">
                  {YEARS.map(y => (
                    <SelectItem key={y.value} value={y.value} className="text-white hover:bg-[#262626]">{y.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="flex items-center gap-2 px-4 py-2 bg-[#121212] border border-[#262626]">
                <TrendingUp className="w-4 h-4 text-[#FF5F1F]" />
                <span className="text-[#9CA3AF] text-sm">{t('expenses.total')}:</span>
                <span className="text-white font-mono font-bold">{formatCurrency(totaleSpese, user?.currency || 'CHF')}</span>
              </div>
            </div>

            {/* Expense List */}
            <div className="space-y-3">
              {filteredSpese.length === 0 ? (
                <div className="text-center py-12 bg-[#121212] border border-[#262626]">
                  <Wallet className="w-12 h-12 text-[#262626] mx-auto mb-4" />
                  <p className="text-[#9CA3AF]">{t('expenses.noExpenses')}</p>
                </div>
              ) : (
                filteredSpese.map((spesa) => (
                  <div key={spesa.id} className="bg-[#121212] border border-[#262626] p-4 hover:border-[#FF5F1F]/30 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="px-2 py-0.5 bg-[#FF5F1F]/20 text-[#FF5F1F] text-xs uppercase font-bold">
                            {spesa.categoria}
                          </span>
                          {spesa.moto_id && (
                            <span className="text-[#9CA3AF] text-xs">{getMotoName(spesa.moto_id)}</span>
                          )}
                          {spesa.categoria === 'Carburante' && spesa.litri && (
                            <span className="text-green-400 text-xs font-mono">
                              {spesa.litri}L {spesa.consumo_calcolato && `• ${spesa.consumo_calcolato} km/L`}
                            </span>
                          )}
                        </div>
                        <p className="text-white">{spesa.descrizione}</p>
                        <p className="text-[#9CA3AF] text-sm flex items-center gap-2">
                          <Calendar className="w-3 h-3" />
                          {spesa.data}
                          {spesa.indirizzo_stazione && (
                            <>
                              <MapPin className="w-3 h-3 ml-2" />
                              {spesa.indirizzo_stazione}
                            </>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="text-[#FF5F1F] font-mono text-xl">{formatCurrency(spesa.importo, spesa.valuta)}</p>
                        <div className="flex gap-1">
                          <button onClick={() => handleOpenDialog(spesa)} className="p-2 text-[#9CA3AF] hover:text-[#FF5F1F]">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(spesa.id)} className="p-2 text-[#9CA3AF] hover:text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Fuel History Tab - Storico Rifornimenti con OCR */}
          <TabsContent value="fuel-history">
            <div className="space-y-6">
              {/* Quick Add with OCR */}
              <div className="bg-gradient-to-r from-green-900/30 to-green-800/20 border border-green-600/30 p-4 rounded-lg">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <h3 className="text-white font-semibold flex items-center gap-2 mb-1">
                      <Camera className="w-5 h-5 text-green-400" />
                      Aggiungi Rifornimento con OCR
                    </h3>
                    <p className="text-[#9CA3AF] text-sm">
                      Scatta una foto al display della pompa e i dati verranno inseriti automaticamente
                    </p>
                  </div>
                  <Button 
                    onClick={handleOpenFuelDialog}
                    disabled={!selectedMotoId}
                    className="bg-green-600 hover:bg-green-700 text-white whitespace-nowrap"
                  >
                    <Fuel className="w-4 h-4 mr-2" />
                    + Nuovo Rifornimento
                  </Button>
                </div>
                {!selectedMotoId && (
                  <p className="text-yellow-400 text-xs mt-2">
                    ⚠️ Seleziona una moto dal menu per registrare un rifornimento
                  </p>
                )}
              </div>

              {/* Fuel Stats Summary */}
              {fuelStats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="bg-[#121212] border border-[#262626] p-4 text-center rounded">
                    <Fuel className="w-5 h-5 text-green-400 mx-auto mb-1" />
                    <p className="text-2xl font-mono text-white">{fuelStats.total_refuels || 0}</p>
                    <p className="text-[#9CA3AF] text-xs">Rifornimenti</p>
                  </div>
                  <div className="bg-[#121212] border border-[#262626] p-4 text-center rounded">
                    <GaugeCircle className="w-5 h-5 text-[#FF5F1F] mx-auto mb-1" />
                    <p className="text-2xl font-mono text-white">{fuelStats.avg_consumption_km_l || '-'}</p>
                    <p className="text-[#9CA3AF] text-xs">km/L medio</p>
                  </div>
                  <div className="bg-[#121212] border border-[#262626] p-4 text-center rounded">
                    <TrendingUp className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
                    <p className="text-2xl font-mono text-white">{fuelStats.total_liters || 0}</p>
                    <p className="text-[#9CA3AF] text-xs">Litri totali</p>
                  </div>
                  <div className="bg-[#121212] border border-[#262626] p-4 text-center rounded">
                    <Wallet className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                    <p className="text-2xl font-mono text-white">{fuelStats.total_spent?.toFixed(0) || 0}</p>
                    <p className="text-[#9CA3AF] text-xs">{user?.currency || 'CHF'} spesi</p>
                  </div>
                  <div className="bg-[#121212] border border-[#262626] p-4 text-center rounded">
                    <BarChart3 className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                    <p className="text-2xl font-mono text-white">{fuelStats.cost_per_km || '-'}</p>
                    <p className="text-[#9CA3AF] text-xs">{user?.currency || 'CHF'}/km</p>
                  </div>
                </div>
              )}

              {/* Consumption Chart */}
              {fuelStats?.consumption_history && fuelStats.consumption_history.length > 0 && (
                <div className="bg-[#121212] border border-[#262626] p-4 rounded">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <GaugeCircle className="w-5 h-5 text-[#FF5F1F]" />
                    Andamento Consumi (km/L)
                  </h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={fuelStats.consumption_history}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fill: '#9CA3AF', fontSize: 11 }}
                        tickFormatter={(val) => val.slice(0, 5)}
                      />
                      <YAxis 
                        tick={{ fill: '#9CA3AF', fontSize: 11 }}
                        domain={['dataMin - 2', 'dataMax + 2']}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#121212', border: '1px solid #262626' }}
                        labelStyle={{ color: '#fff' }}
                        formatter={(value) => [`${value} km/L`, 'Consumo']}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="consumption" 
                        stroke="#FF5F1F" 
                        strokeWidth={2}
                        dot={{ fill: '#FF5F1F', strokeWidth: 2 }}
                        activeDot={{ r: 6, fill: '#FF5F1F' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Fuel History List */}
              <div>
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#9CA3AF]" />
                  Storico Rifornimenti
                </h3>
                <div className="space-y-2">
                  {spese.filter(s => s.categoria?.toLowerCase() === 'carburante').length === 0 ? (
                    <div className="text-center py-12 bg-[#121212] border border-[#262626] rounded">
                      <Fuel className="w-12 h-12 text-[#262626] mx-auto mb-4" />
                      <p className="text-[#9CA3AF]">Nessun rifornimento registrato</p>
                      <p className="text-[#666] text-sm mt-1">
                        Clicca su "+ Nuovo Rifornimento" per iniziare a tracciare i consumi
                      </p>
                    </div>
                  ) : (
                    spese
                      .filter(s => s.categoria?.toLowerCase() === 'carburante')
                      .sort((a, b) => {
                        // Sort by date descending (DD.MM.YYYY format)
                        const dateA = a.data?.split('.').reverse().join('') || '';
                        const dateB = b.data?.split('.').reverse().join('') || '';
                        return dateB.localeCompare(dateA);
                      })
                      .map((rifornimento, index) => (
                        <div 
                          key={rifornimento.id} 
                          className="bg-[#121212] border border-[#262626] p-4 rounded hover:border-green-600/30 transition-colors"
                        >
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-green-600/20 rounded-full flex items-center justify-center">
                                <Fuel className="w-5 h-5 text-green-400" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-white font-medium">
                                    {rifornimento.litri ? `${rifornimento.litri} L` : rifornimento.descrizione}
                                  </span>
                                  {rifornimento.consumo_calcolato && (
                                    <span className="px-2 py-0.5 bg-[#FF5F1F]/20 text-[#FF5F1F] text-xs font-mono rounded">
                                      {rifornimento.consumo_calcolato} km/L
                                    </span>
                                  )}
                                  {rifornimento.km_contachilometri && (
                                    <span className="text-[#9CA3AF] text-xs font-mono">
                                      @ {rifornimento.km_contachilometri.toLocaleString()} km
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 text-[#9CA3AF] text-sm mt-1">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {rifornimento.data}
                                  </span>
                                  {rifornimento.indirizzo_stazione && (
                                    <span className="flex items-center gap-1 truncate max-w-[200px]">
                                      <MapPin className="w-3 h-3" />
                                      {rifornimento.indirizzo_stazione}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-green-400 font-mono text-lg">
                                  {formatCurrency(rifornimento.importo, rifornimento.valuta)}
                                </p>
                                {rifornimento.prezzo_litro && (
                                  <p className="text-[#666] text-xs font-mono">
                                    {rifornimento.prezzo_litro.toFixed(3)} {rifornimento.valuta}/L
                                  </p>
                                )}
                              </div>
                              <div className="flex gap-1">
                                <button 
                                  onClick={() => handleOpenDialog(rifornimento)} 
                                  className="p-2 text-[#9CA3AF] hover:text-[#FF5F1F] transition-colors"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button 
                                  onClick={() => handleDelete(rifornimento.id)} 
                                  className="p-2 text-[#9CA3AF] hover:text-red-500 transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Report Tab */}
          <TabsContent value="report">
            <div className="space-y-6">
              {/* Period selector and download */}
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex gap-2">
                  <Button 
                    variant={reportPeriod === 'month' ? 'default' : 'outline'}
                    onClick={() => setReportPeriod('month')}
                    className={reportPeriod === 'month' ? 'bg-[#FF5F1F]' : 'border-[#262626] text-white'}
                  >
                    {t('expenses.report.monthly')}
                  </Button>
                  <Button 
                    variant={reportPeriod === 'year' ? 'default' : 'outline'}
                    onClick={() => setReportPeriod('year')}
                    className={reportPeriod === 'year' ? 'bg-[#FF5F1F]' : 'border-[#262626] text-white'}
                  >
                    {t('expenses.report.yearly')}
                  </Button>
                </div>
                <Button onClick={generatePDF} variant="outline" className="border-[#FF5F1F] text-[#FF5F1F]">
                  <Download className="w-4 h-4 mr-2" />
                  {t('expenses.report.downloadPDF')}
                </Button>
              </div>

              {/* Fuel Stats Cards */}
              {fuelStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-[#121212] border border-[#262626] p-4 text-center">
                    <Fuel className="w-6 h-6 text-green-400 mx-auto mb-2" />
                    <p className="text-2xl font-mono text-white">{fuelStats.total_refuels}</p>
                    <p className="text-[#9CA3AF] text-xs uppercase">{t('expenses.fuel.totalRefuels')}</p>
                  </div>
                  <div className="bg-[#121212] border border-[#262626] p-4 text-center">
                    <GaugeCircle className="w-6 h-6 text-[#FF5F1F] mx-auto mb-2" />
                    <p className="text-2xl font-mono text-white">{fuelStats.avg_consumption_km_l || '-'}</p>
                    <p className="text-[#9CA3AF] text-xs uppercase">km/L</p>
                  </div>
                  <div className="bg-[#121212] border border-[#262626] p-4 text-center">
                    <TrendingUp className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                    <p className="text-2xl font-mono text-white">{fuelStats.total_liters}</p>
                    <p className="text-[#9CA3AF] text-xs uppercase">{t('expenses.fuel.totalLiters')}</p>
                  </div>
                  <div className="bg-[#121212] border border-[#262626] p-4 text-center">
                    <Wallet className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                    <p className="text-2xl font-mono text-white">{fuelStats.cost_per_km || '-'}</p>
                    <p className="text-[#9CA3AF] text-xs uppercase">{user?.currency || 'CHF'}/km</p>
                  </div>
                </div>
              )}

              {/* Charts */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Pie Chart - Category breakdown */}
                <div className="bg-[#121212] border border-[#262626] p-4">
                  <h3 className="text-white font-semibold mb-4">{t('expenses.report.byCategory')}</h3>
                  {pieChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#121212', border: '1px solid #262626' }}
                          labelStyle={{ color: '#fff' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-[#9CA3AF]">
                      {t('expenses.report.noData')}
                    </div>
                  )}
                </div>

                {/* Bar Chart - Monthly/Yearly trend */}
                <div className="bg-[#121212] border border-[#262626] p-4">
                  <h3 className="text-white font-semibold mb-4">{t('expenses.report.trend')}</h3>
                  {barChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={barChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                        <XAxis dataKey="period" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                        <YAxis tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#121212', border: '1px solid #262626' }}
                          labelStyle={{ color: '#fff' }}
                        />
                        <Bar dataKey="totale" fill="#FF5F1F" name={t('expenses.total')} />
                        <Bar dataKey="carburante" fill="#22C55E" name={t('expenses.fuel.title')} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-[#9CA3AF]">
                      {t('expenses.report.noData')}
                    </div>
                  )}
                </div>
              </div>

              {/* Grand Total */}
              {reportData && (
                <div className="bg-[#FF5F1F]/10 border border-[#FF5F1F] p-6 text-center">
                  <p className="text-[#9CA3AF] uppercase text-sm mb-2">{t('expenses.report.grandTotal')}</p>
                  <p className="text-4xl font-mono text-[#FF5F1F] font-bold">
                    {formatCurrency(reportData.grand_total || 0, user?.currency || 'CHF')}
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Fuel Dialog */}
        <Dialog open={fuelDialogOpen} onOpenChange={setFuelDialogOpen}>
          <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Teko'] text-2xl text-white uppercase flex items-center gap-2">
                <Fuel className="w-6 h-6 text-green-500" />
                {t('expenses.fuel.title')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleFuelSubmit} className="space-y-4">
              {/* Moto selector */}
              <div>
                <Label className="form-label">{t('common.motorcycle')} *</Label>
                <Select value={fuelData.moto_id} onValueChange={(v) => setFuelData({...fuelData, moto_id: v})}>
                  <SelectTrigger className="form-input"><SelectValue placeholder={t('common.select')} /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#262626]">
                    {motoList.map(moto => (
                      <SelectItem key={moto.id} value={moto.id} className="text-white hover:bg-[#262626]">
                        {moto.marca} {moto.modello}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* KM and Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">{t('expenses.fuel.currentKm')} *</Label>
                  <Input 
                    type="number"
                    value={fuelData.km_contachilometri}
                    onChange={(e) => setFuelData({...fuelData, km_contachilometri: parseInt(e.target.value) || 0})}
                    onFocus={(e) => e.target.select()}
                    className="form-input"
                  />
                </div>
                <div>
                  <Label className="form-label">{t('expenses.date')} *</Label>
                  <Input 
                    value={fuelData.data}
                    onChange={(e) => setFuelData({...fuelData, data: e.target.value})}
                    placeholder="DD.MM.YYYY"
                    className="form-input"
                  />
                </div>
              </div>

              {/* OCR Camera for fuel receipt */}
              <div className="bg-[#1a1a1a] border border-[#262626] rounded p-3">
                <OcrCamera 
                  type="fuel"
                  buttonText="📷 Foto display pompa"
                  buttonClassName="w-full h-10 bg-[#262626] hover:bg-[#363636] text-[#9CA3AF] hover:text-white border border-[#262626] hover:border-[#FF5F1F] transition-colors"
                  onDataExtracted={(data) => {
                    const updates = {};
                    if (data.liters !== null) updates.litri = data.liters;
                    if (data.total_cost !== null) updates.importo = data.total_cost;
                    if (data.price_per_liter !== null) updates.prezzo_litro = data.price_per_liter;
                    
                    if (Object.keys(updates).length > 0) {
                      setFuelData(prev => ({...prev, ...updates}));
                      toast.success('Dati importati automaticamente');
                    }
                  }}
                />
                <p className="text-xs text-[#666] mt-2 text-center">
                  Scatta una foto al display della pompa di benzina
                </p>
              </div>

              {/* Liters and Price */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="form-label">{t('expenses.fuel.liters')} *</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={fuelData.litri}
                    onChange={(e) => setFuelData({...fuelData, litri: parseFloat(e.target.value) || 0})}
                    onFocus={(e) => e.target.select()}
                    onBlur={calculatePricePerLiter}
                    className="form-input"
                  />
                </div>
                <div>
                  <Label className="form-label">{t('expenses.fuel.totalPrice')} *</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={fuelData.importo}
                    onChange={(e) => setFuelData({...fuelData, importo: parseFloat(e.target.value) || 0})}
                    onFocus={(e) => e.target.select()}
                    onBlur={calculatePricePerLiter}
                    className="form-input"
                  />
                </div>
                <div>
                  <Label className="form-label">{t('expenses.fuel.pricePerLiter')}</Label>
                  <Input 
                    type="number"
                    step="0.001"
                    value={fuelData.prezzo_litro}
                    onChange={(e) => setFuelData({...fuelData, prezzo_litro: parseFloat(e.target.value) || 0})}
                    onFocus={(e) => e.target.select()}
                    onBlur={calculateTotal}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Fill type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">{t('expenses.fuel.fillType')}</Label>
                  <Select value={fuelData.tipo_pieno} onValueChange={(v) => setFuelData({...fuelData, tipo_pieno: v})}>
                    <SelectTrigger className="form-input"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#262626]">
                      <SelectItem value="completo" className="text-white hover:bg-[#262626]">{t('expenses.fuel.fullTank')}</SelectItem>
                      <SelectItem value="parziale" className="text-white hover:bg-[#262626]">{t('expenses.fuel.partialTank')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">{t('expenses.currency')}</Label>
                  <Select value={fuelData.valuta} onValueChange={(v) => setFuelData({...fuelData, valuta: v})}>
                    <SelectTrigger className="form-input"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#262626]">
                      {VALUTE.map(v => (
                        <SelectItem key={v.code} value={v.code} className="text-white hover:bg-[#262626]">{v.code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Last fill not registered checkbox */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="lastFillNotRegistered"
                  checked={fuelData.ultimo_pieno_non_registrato}
                  onChange={(e) => setFuelData({...fuelData, ultimo_pieno_non_registrato: e.target.checked})}
                  className="w-4 h-4 accent-[#FF5F1F]"
                />
                <Label htmlFor="lastFillNotRegistered" className="text-white text-sm cursor-pointer">
                  {t('expenses.fuel.lastFillNotRegistered')}
                </Label>
              </div>

              {/* GPS Location */}
              <div>
                <Label className="form-label">{t('expenses.fuel.stationLocation')}</Label>
                <div className="flex gap-2">
                  <Input 
                    value={fuelData.indirizzo_stazione}
                    onChange={(e) => setFuelData({...fuelData, indirizzo_stazione: e.target.value})}
                    placeholder={t('expenses.fuel.stationPlaceholder')}
                    className="form-input flex-1"
                  />
                  <Button 
                    type="button"
                    onClick={getCurrentLocation}
                    disabled={gettingLocation}
                    variant="outline"
                    className="border-[#262626]"
                  >
                    {gettingLocation ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <MapPin className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {fuelData.latitudine && (
                  <p className="text-green-400 text-xs mt-1">
                    GPS: {fuelData.latitudine.toFixed(6)}, {fuelData.longitudine.toFixed(6)}
                  </p>
                )}
              </div>

              <Button type="submit" disabled={saving} className="w-full bg-green-600 hover:bg-green-700">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {t('expenses.fuel.save')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
